import { normalizeExportPath } from '@couplespace/core/notion-export';

export type FolderFileScan = {
  files: Map<string, File>;
  paths: string[];
  rootName: string;
};

async function walkDirectory(
  dir: FileSystemDirectoryHandle,
  prefix: string,
  paths: string[],
  files: Map<string, File>,
): Promise<void> {
  for await (const [name, handle] of dir.entries()) {
    const path = prefix ? `${prefix}/${name}` : name;
    if (handle.kind === 'file') {
      const file = await handle.getFile();
      const norm = normalizeExportPath(path);
      files.set(norm, file);
      paths.push(norm);
    } else if (handle.kind === 'directory') {
      await walkDirectory(handle, path, paths, files);
    }
  }
}

export async function scanFolderDirectoryHandle(
  dir: FileSystemDirectoryHandle,
): Promise<FolderFileScan> {
  const files = new Map<string, File>();
  const paths: string[] = [];
  await walkDirectory(dir, '', paths, files);
  return {
    files,
    paths,
    rootName: dir.name,
  };
}

export async function scanFolderFileList(fileList: FileList): Promise<FolderFileScan> {
  const files = new Map<string, File>();
  const paths: string[] = [];
  let rootName = 'Import';

  for (const file of fileList) {
    const rel = file.webkitRelativePath || file.name;
    const segments = rel.split('/');
    if (segments.length > 1 && rootName === 'Import') {
      rootName = segments[0] ?? rootName;
    }
    const path = normalizeExportPath(rel.replace(/^[^/]+\//, ''));
    if (!path) {
      continue;
    }
    files.set(path, file);
    paths.push(path);
  }

  return { files, paths, rootName };
}

export async function readTextFile(files: Map<string, File>, path: string): Promise<string | null> {
  const norm = normalizeExportPath(path);
  const direct = files.get(norm);
  if (direct) {
    return direct.text();
  }
  for (const [key, file] of files) {
    if (key === norm || key.endsWith(`/${norm}`) || norm.endsWith(key)) {
      return file.text();
    }
  }
  return null;
}

export function resolveFilePath(files: Map<string, File>, path: string): string | null {
  const norm = normalizeExportPath(path);
  if (files.has(norm)) {
    return norm;
  }
  for (const key of files.keys()) {
    if (key === norm || key.endsWith(`/${norm}`) || norm.endsWith(key)) {
      return key;
    }
  }
  return null;
}
